import { toast } from "sonner";
import { fetchFastApi } from "../../api/fastapi";
import {
  attributesValidator,
  rowKeysValidator,
  tableValidator,
} from "../../models/schemasV2";
import {
  HandleColumnActionParams,
  HandleTableActionParams,
  HandleRowKeyActionParams,
  Template,
  HandleTableOrderActionParams,
} from "./types";

export const handleTableAction = async (
  templateId: string,
  setTemplate: React.Dispatch<React.SetStateAction<Template | null>>,
  { tableId, tableName, description, rowKeyColumnId }: HandleTableActionParams,
) => {
  if (!tableName) {
    toast.error("Please fill in all fields");
    return;
  }

  const isNewTable = !tableId;
  const actionTableId = tableId || crypto.randomUUID();

  const newTable = tableValidator.parse({
    id: actionTableId,
    name: tableName,
    description: description,
    sourceDescription: "",
    rowKeyColumnId: rowKeyColumnId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  setTemplate((prevTemplate) => {
    if (!prevTemplate) return null;
    if (isNewTable) {
      return {
        ...prevTemplate,
        tables: [...prevTemplate.tables, newTable],
      };
    } else {
      const oldTable = prevTemplate.tables.find(
        (table) => table.id === tableId,
      );
      const oldAttributes = oldTable?.attributes;
      const oldRowKeys = oldTable?.rowKeys;
      const rowKeyColumn = oldTable?.rowKeyColumnId;

      newTable.attributes = oldAttributes || [];
      newTable.rowKeys = oldRowKeys || [];
      if (!rowKeyColumn && newTable.rowKeyColumnId) {
        newTable.attributes.push({
          id: newTable.rowKeyColumnId,
          name: "Row Key",
          tableId: tableId,
          valueGroupId: null,
          description: "",
          type: "string",
          required: true,
          inferred: false,
          enum: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      if (rowKeyColumn && !newTable.rowKeyColumnId) {
        newTable.attributes = newTable.attributes.filter(
          (attribute) => attribute.id !== rowKeyColumn,
        );
      }

      return {
        ...prevTemplate,
        tables: prevTemplate.tables.map((table) =>
          table.id === tableId ? newTable : table,
        ),
      };
    }
  });

  try {
    await fetchFastApi({
      path: `/schemas/v2/schema/${templateId}/table_action`,
      method: "POST",
      body: {
        table: newTable,
        action: isNewTable ? "add" : "edit",
      },
    });
  } catch (error) {
    toast.error("An error occurred while creating table");
    console.error("Error creating table:", error);
    // Revert optimistic update
    setTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      return {
        ...prevTemplate,
        tables: prevTemplate.tables.filter((table) => table.id !== tableId),
      };
    });
  }
};

export const handleColumnAction = async (
  templateId: string,
  setTemplate: React.Dispatch<React.SetStateAction<Template | null>>,
  {
    tableId,
    columnId,
    columnName,
    description,
    deleteColumn,
    required,
    inferred,
    columnType,
    unit,
    presets,
    columnOrder,
  }: HandleColumnActionParams,
) => {
  if (!tableId || !columnName) {
    toast.error("Please fill in all fields");
    return;
  }

  const isNewColumn = !columnId;
  const actionColumnId = columnId || crypto.randomUUID();

  const columnData = attributesValidator.parse({
    id: actionColumnId,
    name: columnName,
    tableId: tableId,
    valueGroupId: null,
    description: description,
    type: columnType,
    unit: unit,
    required: required,
    inferred: inferred,
    enum: presets,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    attributeOrder: columnOrder,
  });

  // Optimistic update
  setTemplate((prevTemplate) => {
    if (!prevTemplate) return null;
    return {
      ...prevTemplate,
      tables: prevTemplate.tables.map((table) => {
        if (table.id === tableId) {
          if (deleteColumn) {
            return {
              ...table,
              attributes: table.attributes.filter(
                (attribute) => attribute.id !== actionColumnId,
              ),
            };
          } else if (isNewColumn) {
            return {
              ...table,
              attributes: [...table.attributes, columnData],
            };
          } else {
            return {
              ...table,
              attributes: table.attributes.map((attribute) =>
                attribute.id === actionColumnId ? columnData : attribute,
              ),
            };
          }
        }
        return table;
      }),
    };
  });

  try {
    let apiPath = `/schemas/v2/schema/${templateId}/table/${tableId}`;
    let method: "POST" | "PUT" | "DELETE" = "POST";
    let body: { attribute: typeof columnData } | undefined = undefined;

    if (deleteColumn) {
      apiPath += `/attribute/${actionColumnId}`;
      method = "DELETE";
    } else if (isNewColumn) {
      apiPath += "/add_attribute";
      method = "POST";
      body = { attribute: columnData };
    } else {
      apiPath += `/attribute/${actionColumnId}`;
      method = "PUT";
      body = { attribute: columnData };
    }

    await fetchFastApi({
      path: apiPath,
      method,
      body,
    });
  } catch (error) {
    toast.error(
      `An error occurred while ${isNewColumn ? "adding" : "editing"} column`,
    );
    console.error(`Error ${isNewColumn ? "adding" : "editing"} column:`, error);
    // Revert optimistic update
    setTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      return {
        ...prevTemplate,
        tables: prevTemplate.tables.map((table) => {
          if (table.id === tableId) {
            if (deleteColumn) {
              return {
                ...table,
                attributes: [...table.attributes, columnData],
              };
            } else if (isNewColumn) {
              return {
                ...table,
                attributes: table.attributes.filter(
                  (attribute) => attribute.id !== actionColumnId,
                ),
              };
            } else {
              return {
                ...table,
                attributes: table.attributes.map((attribute) =>
                  attribute.id === actionColumnId
                    ? { ...attribute, name: columnName, description }
                    : attribute,
                ),
              };
            }
          }
          return table;
        }),
      };
    });
  }
};

export const handleRowKeyAction = async (
  templateId: string,
  setTemplate: React.Dispatch<React.SetStateAction<Template | null>>,
  {
    tableId,
    rowKeyId,
    rowKeyName,
    deleteRowKey,
    rowKeyDescription,
    rowKeyRequired,
  }: HandleRowKeyActionParams,
) => {
  if (!tableId || !rowKeyName) {
    toast.error("Please fill in all fields");
    return;
  }

  const isNewRowKey = !rowKeyId;
  const actionRowKeyId = rowKeyId || crypto.randomUUID();

  const rowKeyData = rowKeysValidator.parse({
    id: actionRowKeyId,
    tableId: tableId,
    name: rowKeyName,
    description: rowKeyDescription,
    required: rowKeyRequired,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  setTemplate((prevTemplate) => {
    if (!prevTemplate) return null;
    return {
      ...prevTemplate,
      tables: prevTemplate.tables.map((table) => {
        if (table.id === tableId) {
          if (deleteRowKey) {
            return {
              ...table,
              rowKeys: (table.rowKeys || []).filter(
                (rowKey) => rowKey.id !== actionRowKeyId,
              ),
            };
          } else if (isNewRowKey) {
            return {
              ...table,
              rowKeys: [...(table.rowKeys || []), rowKeyData],
            };
          } else {
            return {
              ...table,
              rowKeys: (table.rowKeys || []).map((rowKey) =>
                rowKey.id === actionRowKeyId ? rowKeyData : rowKey,
              ),
            };
          }
        }
        return table;
      }),
    };
  });

  let apiPath = `/schemas/v2/schema/${templateId}/table/${tableId}/row_key`;
  let method: "POST" | "PUT" | "DELETE" = "POST";
  if (isNewRowKey) {
    method = "POST";
  } else if (deleteRowKey) {
    apiPath += `/${actionRowKeyId}`;
    method = "DELETE";
  } else {
    method = "PUT";
    apiPath += `/${actionRowKeyId}`;
  }

  try {
    await fetchFastApi({
      path: apiPath,
      method,
      body: deleteRowKey ? undefined : { row_key: rowKeyData },
    });
  } catch (error) {
    toast.error(
      `An error occurred while ${isNewRowKey ? "adding" : "editing"} row key`,
    );
    console.error(
      `Error ${isNewRowKey ? "adding" : "editing"} row key:`,
      error,
    );
  }
};

export const handleDeleteTable = async (
  templateId: string,
  setTemplate: React.Dispatch<React.SetStateAction<Template | null>>,
  tableId: string,
) => {
  setTemplate((prevTemplate) => {
    if (!prevTemplate) return null;
    return {
      ...prevTemplate,
      tables: prevTemplate.tables.filter((table) => table.id !== tableId),
    };
  });

  try {
    await fetchFastApi({
      path: `/schemas/v2/schema/${templateId}/table/${tableId}`,
      method: "DELETE",
    });
  } catch (error) {
    toast.error("An error occurred while deleting table");
    console.error("Error deleting table:", error);
  }
};

export const handleTableOrderAction = async (
  templateId: string,
  setTemplate: React.Dispatch<React.SetStateAction<Template | null>>,
  { tableId, attributesOrder }: HandleTableOrderActionParams,
) => {
  setTemplate((prevTemplate) => {
    if (!prevTemplate) return null;
    return {
      ...prevTemplate,
      tables: prevTemplate.tables.map((table) => {
        if (table.id === tableId) {
          return {
            ...table,
            attributes: table.attributes.map((attribute) => ({
              ...attribute,
              attributeOrder: attributesOrder.indexOf(attribute.id),
            })),
          };
        }
        return table;
      }),
    };
  });

  try {
    await fetchFastApi({
      path: `/schemas/v2/schema/${templateId}/table/${tableId}/update_attribute_order`,
      method: "POST",
      body: { new_attribute_order: attributesOrder },
    });
  } catch (error) {
    toast.error("An error occurred while updating attribute order");
    console.error("Error updating attribute order:", error);
  }
};
